import { useContext, useRef, useState, useLayoutEffect } from "react";
import { motion, useAnimationControls, type Transition } from "framer-motion";
import Card, { type CardData } from "./Card";
import { DeckPositionContext } from "./DeckPositionContext";
import { cn } from "@/lib/utils";

interface AnimatedCardProps {
  card?: CardData;
  faceDown?: boolean;
  compact?: boolean;
  mini?: boolean;
  className?: string;
  shouldDeal?: boolean;
  onDealComplete?: () => void;
}

const dealSpring: Transition = {
  type: "spring",
  stiffness: 170,
  damping: 20,
  mass: 0.8,
};

export default function AnimatedCard({
  card,
  faceDown = false,
  compact = false,
  mini = false,
  className,
  shouldDeal = false,
  onDealComplete,
}: AnimatedCardProps) {
  const { getDeckRect } = useContext(DeckPositionContext);
  const containerRef = useRef<HTMLDivElement>(null);
  const controls = useAnimationControls();
  const [isDealing, setIsDealing] = useState(shouldDeal);

  useLayoutEffect(() => {
    if (!isDealing || !containerRef.current) return;

    const deckRect = getDeckRect();
    const cardRect = containerRef.current.getBoundingClientRect();

    if (deckRect) {
      const dx = deckRect.left + deckRect.width / 2 - (cardRect.left + cardRect.width / 2);
      const dy = deckRect.top + deckRect.height / 2 - (cardRect.top + cardRect.height / 2);

      controls.set({ x: dx, y: dy, rotateY: 180, scale: 0.85 });
      controls.start({
        x: 0,
        y: 0,
        rotateY: 0,
        scale: 1,
        transition: dealSpring,
      }).then(() => {
        setIsDealing(false);
        onDealComplete?.();
      });
    } else {
      setIsDealing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isDealing) {
    return (
      <div
        ref={containerRef}
        className={cn("inline-block", className)}
        style={{ perspective: 800 }}
      >
        <motion.div
          animate={controls}
          className="relative"
          style={{ transformStyle: "preserve-3d" }}
        >
          <div style={{ backfaceVisibility: "hidden" }}>
            <Card card={card} compact={compact} mini={mini} />
          </div>
          <div
            className="absolute inset-0"
            style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
          >
            <Card faceDown compact={compact} mini={mini} />
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <motion.div
      ref={containerRef}
      layout
      className={cn("inline-block", className)}
      whileHover={
        mini
          ? undefined
          : {
              scale: 1.02,
              filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3))",
            }
      }
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
    >
      <Card card={card} faceDown={faceDown} compact={compact} mini={mini} />
    </motion.div>
  );
}
